import {
  FALLBACK_QUESTIONS,
  FALLBACK_REFLECTIONS,
} from "../lib/constants";
import type { AIProvider } from "./provider";
import type {
  ActionResult,
  NextQuestion,
  QuestionAnalysis,
  ReflectionResult,
  Topic,
} from "../types/journey";

export function inferTopic(question: string): Topic {
  if (/创新|新方法|尝试|现成答案|改变/.test(question)) return "innovation";
  if (/努力|坚持|继续|结果|长期|放弃/.test(question)) return "persistence";
  if (/集体|责任|需要|他人|得失|帮助/.test(question)) return "responsibility";
  if (/服务|倾听|帮助/.test(question)) return "service";
  if (/成长|学习|提升/.test(question)) return "growth";
  if (/合作|一起|伙伴/.test(question)) return "collaboration";
  return "uncertainty";
}

export function fallbackAnalysis(question: string): QuestionAnalysis {
  const topic = inferTopic(question);
  const preset = FALLBACK_QUESTIONS[topic as keyof typeof FALLBACK_QUESTIONS] ?? FALLBACK_QUESTIONS.innovation;
  return preset.first;
}

export function fallbackNextQuestion(topic: Topic): NextQuestion {
  const preset = FALLBACK_QUESTIONS[topic as keyof typeof FALLBACK_QUESTIONS] ?? FALLBACK_QUESTIONS.innovation;
  return { questionTwo: preset.second };
}

export function fallbackReflection(topic: Topic): ReflectionResult {
  const safeTopic = topic in FALLBACK_REFLECTIONS ? topic : "other";
  const reflection = FALLBACK_REFLECTIONS[safeTopic].length >= 100
    ? FALLBACK_REFLECTIONS[safeTopic]
    : `${FALLBACK_REFLECTIONS[safeTopic]}答案不必一次完成，先让下一步告诉你更多，也给自己留出调整方法和节奏的空间。`;
  return {
    reflection,
    topic,
    recommendedTheme:
      topic === "uncertainty" ? "other" : (topic as ReflectionResult["recommendedTheme"]),
  };
}

export function refineActionLocally(original: string): ActionResult {
  const cleaned = original.replace(/[。！!]+$/u, "").trim();
  const normalized = cleaned
    .replace(/^明天我会/u, "")
    .replace(/^我会/u, "")
    .replace(/^我愿意/u, "")
    .trim();
  const refined = normalized.length > 24 ? `${normalized.slice(0, 23)}…` : normalized;
  return { original, refined: refined || "先完成一小步" };
}

export class MockAIProvider implements AIProvider {
  async analyzeQuestion(question: string): Promise<QuestionAnalysis> {
    return fallbackAnalysis(question);
  }

  async generateNextQuestion(input: { topic: Topic; selectedOption: "A" | "B" }): Promise<NextQuestion> {
    return fallbackNextQuestion(input.topic);
  }

  async generateReflection(input: {
    originalQuestion: string;
    firstAnswer: string;
    secondAnswer: string;
    thirdAnswer: string;
  }): Promise<ReflectionResult> {
    return fallbackReflection(inferTopic(input.originalQuestion));
  }

  async refineAction(original: string): Promise<ActionResult> {
    return refineActionLocally(original);
  }
}
